import { observer } from "mobx-react";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faMusic, 
  faGlobe, 
  faExternalLinkAlt, 
  faCalendarAlt, 
  faInfoCircle,
  faClock,
  faTicketAlt,
  faShare,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { runInAction } from "mobx";

import { RootStoreContext } from "../../../stores/RootStore";
import EventTicketOptions from "./EventTicketOptions";
import ConfirmButton from "./ConfirmButton";
import { Event } from "../../../config/Registration";
import { formatStyles, formatToTimezone } from "../../../util";
import { buildEventShareData, buildEventShareUrl, copyEventShareUrl, shouldUseNativeShare } from "../../../util/eventShare";

interface EventTicketsSectionProps {
  event: Event;
  tickets: any[];
  formatEventDate: (startTime: string, endTime: string) => string;
}

const EventTicketsSection = observer(({ event, tickets, formatEventDate }: EventTicketsSectionProps) => {
  const { registrationStore, eventPhotoStore, studioConfigStore } = useContext(RootStoreContext);
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const shareStatusTimeout = useRef<number | null>(null);
  const usesNativeShare = shouldUseNativeShare();

  const showShareStatus = (status: 'copied' | 'error') => {
    setShareStatus(status);
    if (shareStatusTimeout.current !== null) {
      window.clearTimeout(shareStatusTimeout.current);
    }
    shareStatusTimeout.current = window.setTimeout(() => setShareStatus('idle'), 2500);
  };

  const handleShareEvent = async () => {
    const shareUrl = buildEventShareUrl(event.eventId);
    if (usesNativeShare) {
      try {
        await navigator.share(buildEventShareData(shareUrl));
        return;
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }

    try {
      await copyEventShareUrl(shareUrl);
      showShareStatus('copied');
    } catch (error) {
      console.error('Failed to share event link:', error);
      showShareStatus('error');
    }
  };

  useEffect(() => {
    eventPhotoStore.loadEventPhoto(event.eventId);
    return () => {
      eventPhotoStore.clearAllEventPhotos();
    };
  }, [event.eventId, eventPhotoStore]);
  
  useEffect(() => {
    const eventsCategory = registrationStore.sellingItemCategories.find(cat => cat.name === "Events");
    if (eventsCategory) {
      runInAction(() => {
        eventsCategory.tickets = tickets;
      });
    }
  }, [tickets, registrationStore]);

  useEffect(() => () => {
    if (shareStatusTimeout.current !== null) {
      window.clearTimeout(shareStatusTimeout.current);
    }
  }, []);

  return (
    <Row className="justify-content-center">
      <Col md={12}>
        <div className="item-container">
          <div className="event-details-container">
         
            <div className="detail-card">
              <div className="detail-card-header event-main-header">
                <div className="event-header-content">
                  <div className="event-title-row">
                    <h2 className="event-main-title">{event.title}</h2>
                    <div className="event-date-time">
                      <FontAwesomeIcon icon={faCalendarAlt} className="event-header-icon" />
                      <span className="event-date-text">{formatEventDate(event.startTime, event.endTime)}</span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className={`event-share-button ${shareStatus !== 'idle' ? shareStatus : ''}`}
                  onClick={handleShareEvent}
                  aria-label={usesNativeShare ? `Share ${event.title}` : `Copy link for ${event.title}`}
                  title={shareStatus === 'copied'
                    ? 'Link copied'
                    : shareStatus === 'error'
                      ? 'Could not copy link'
                      : usesNativeShare ? 'Share event' : 'Copy event link'}
                >
                  <FontAwesomeIcon icon={shareStatus === 'copied'
                    ? faCheckCircle
                    : shareStatus === 'error' ? faExclamationTriangle : faShare} />
                </button>
                <span
                  className={`event-share-feedback ${shareStatus !== 'idle' ? 'visible' : ''}`}
                  role="status"
                  aria-live="polite"
                >
                  {shareStatus === 'copied' ? 'Link copied' : shareStatus === 'error' ? 'Could not copy link' : ''}
                </span>
              </div>
              {eventPhotoStore.getEventPhoto(event.eventId)?.photoUrl && (
                <div className="detail-item mt-2">
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div style={{ 
                      width: '100%', 
                      paddingTop: '100%', 
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={eventPhotoStore.getEventPhoto(event.eventId)?.photoUrl}
                        alt={event.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="detail-content">
                <div className="detail-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-item-icon" />
                  <div className="detail-item-content">
                    <span className="detail-label">Venue</span>
                    <span className="detail-value">{event.venueName}, {event.venueStreet}, {event.venueCity}</span>
                  </div>
                </div>

                {event.isInternational && (
                  <div className="detail-item">
                      <FontAwesomeIcon icon={faGlobe} className="detail-item-icon" />
                      <div className="detail-item-content">
                        <span className="detail-value">International Artists</span>
                      </div>
                  </div>
                )}
                
                <div className="detail-item">
                  <FontAwesomeIcon icon={faMusic} className="detail-item-icon" />
                  <div className="detail-item-content">
                    <span className="detail-label">Styles</span>
                    <span className="detail-value">{formatStyles(event.styles)}</span>
                  </div>
                </div>

              </div>
            </div>

            {event.purchasedTickets && event.purchasedTickets.length > 0 && (
              <div className="detail-card" id="booked-tickets-section">
                <div className="detail-card-header">
                  <FontAwesomeIcon icon={faCheckCircle} className="section-icon booked-check-icon" />
                  <h4 className="detail-title">Your Booked Tickets</h4>
                </div>
                <div className="detail-content">
                  {event.purchasedTickets.map((purchasedTicket, index) => (
                    <div key={`purchased-ticket-${index}`} className="purchased-ticket-item">
                      <div className="ticket-info">
                        <div className="ticket-name">
                          <FontAwesomeIcon icon={faCheckCircle} className="purchased-icon" />
                          <span className="ticket-title">{purchasedTicket.description}</span>
                        </div>
                        <div className="ticket-details">
                          <span className="ticket-price">
                            {purchasedTicket.paymentMethod === 'dance_coins' ? 'Ȼ' : '€'} {purchasedTicket.price}
                          </span>
                        </div>
                        {purchasedTicket.comment && purchasedTicket.comment !== purchasedTicket.description && (
                          <div className="ticket-comment">{purchasedTicket.comment}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-card" id="tickets-section">
              <div className="detail-card-header">
                  <FontAwesomeIcon icon={faTicketAlt} className="section-icon" />
                  <h4 className="detail-title">Choose your ticket</h4>
                </div>
              {tickets.map((ticket, index) => (
                <EventTicketOptions
                  key={`ticket-${index}`}
                  item={ticket}
                />
              ))}
              <div className="d-flex justify-content-center">
                <ConfirmButton />
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <FontAwesomeIcon icon={faClock} className="detail-icon" />
                <h4 className="detail-title">Schedule</h4>
              </div>
              <div className="detail-content">
                {event.programTimes && event.programTimes.length > 0 &&
                 event.programTimes.some(pt => pt.startTime && pt.endTime && pt.programName) ? (
                  event.programTimes
                    .filter(pt => pt.startTime && pt.endTime && pt.programName)
                    .map((programTime, index) => {
                      const startDate = formatToTimezone(programTime.startTime, 'Europe/Tallinn', 'en-US', {
                        month: 'short',
                        day: '2-digit'
                      });
                      const startTime = formatToTimezone(programTime.startTime, 'Europe/Tallinn', 'en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      });
                      const endTime = formatToTimezone(programTime.endTime, 'Europe/Tallinn', 'en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      });

                      return (
                        <div key={`program-${index}`} className="program-item">
                          <div className="program-time">
                            <span className="program-date">{startDate}</span>
                            <span className="program-duration">{startTime} - {endTime}</span>
                          </div>
                          <div className="program-name">{programTime.programName}</div>
                        </div>
                      );
                    })
                ) : (
                  <div className="detail-item">
                    <span className="detail-value">Program schedule not published yet</span>
                  </div>
                )}
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <FontAwesomeIcon icon={faInfoCircle} className="detail-icon" />
                <h4 className="detail-title">Description</h4>
              </div>
              <div className="detail-content">
                <div className="detail-item">
                  <div className="detail-item-content">
                    <span className="detail-value" dangerouslySetInnerHTML={{
                      __html: event.description?.replace(
                        /(https?:\/\/[^\s<]+)/g,
                        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
                      ) || ''
                    }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
                <div className="detail-card-header">
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="detail-icon" />
                  <h4 className="detail-title">Links</h4>
                </div>
                <div className="detail-content">
                    <div className="detail-item">
                      <FontAwesomeIcon icon={faShare} className="detail-item-icon" />
                      <div className="detail-item-content">
                        <span className="detail-label">Tickets</span>
                        <a href={`${studioConfigStore.studioHomepage}/en/event/?id=${event.eventId}`} target="_blank" rel="noopener noreferrer" className="detail-link">
                          View Ticket Information
                        </a>
                      </div>
                    </div>
                  
                  {event.facebookEventUrl && (
                    <div className="detail-item">
                      <FontAwesomeIcon icon={faShare} className="detail-item-icon" />
                      <div className="detail-item-content">
                        <span className="detail-label">Facebook Event</span>
                        <a href={event.facebookEventUrl} target="_blank" rel="noopener noreferrer" className="detail-link">
                          View on Facebook
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>
        </div>
      </Col>
    </Row>
  );
});

export default EventTicketsSection;
